import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';

import { trackInviteLinkClicked } from '@/features/analytics/track/trackInviteEvents';
import { validateInviteOptions } from '@/features/invites/hooks/useValidateInvite/validateInvite.options';
import {
  setPendingInviteData,
  setPendingInviteToken,
} from '@/features/invites/utils/pendingInvite';
import { getMyUserOptions } from '@/features/users/hooks/useGetMyUser/getMyUser.options';
import { setInviteAttribution } from '@/shared/utils/attribution';

const searchSchema = z.object({
  token: z.string(),
});

export const Route = createFileRoute('/invite')({
  validateSearch: searchSchema,
  beforeLoad: async ({ context, search }) => {
    // Note: No mobile web redirect here - we allow mobile web users through
    // so they can complete the invite flow. The token is stored and they
    // proceed to /login where isInInviteFlow() will allow them through.

    const { token } = search;

    setInviteAttribution();

    // Check if user is authenticated
    let isAuthenticated = false;
    try {
      await context.queryClient.ensureQueryData(getMyUserOptions());
      isAuthenticated = true;
    } catch {
      // Not authenticated
    }

    trackInviteLinkClicked(isAuthenticated);

    // If not authenticated, store token and redirect to login
    if (!isAuthenticated) {
      setPendingInviteToken(token);
      throw redirect({
        to: '/login',
        search: {
          utm_source: 'invite',
          utm_medium: 'referral',
          utm_campaign: 'duo_invite',
        },
        replace: true,
      });
    }

    // User is authenticated - validate the invite
    try {
      const validation = await context.queryClient.fetchQuery(
        validateInviteOptions({ token }),
      );

      if (validation.valid) {
        // Store full invite data for the modal
        setPendingInviteData({
          token,
          inviterDisplayName: validation.inviterDisplayName ?? '',
          inviterPictureUrl: validation.inviterPictureUrl ?? '',
        });
      }
      // Note: Invalid invites will be handled by the target route (show toast)
    } catch {
      // Validation failed - let it redirect anyway, the modal won't open
    }

    // Redirect to home - the pending invite will be processed there
    throw redirect({
      to: '/',
      replace: true,
    });
  },
  component: function InvitePage() {
    // This should never render since beforeLoad always redirects
    return null;
  },
});
